/**
 * U.S. phone number validation (10-digit NANP).
 * Keep in sync with Portal-Backend/services/usPhoneValidation.js
 */

export function extractPhoneDigits(raw: unknown): string {
  return String(raw ?? "").replace(/\D/g, "");
}

/** Format up to 10 digits for display as (XXX) XXX-XXXX. */
export function formatPhoneDisplay(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export type UsPhoneValidationResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateUsPhone(raw: unknown): UsPhoneValidationResult {
  const digits = extractPhoneDigits(raw);

  if (digits.length !== 10) {
    return {
      ok: false,
      message: "Enter a valid 10-digit U.S. phone number.",
    };
  }

  const areaFirst = digits[0];
  const exchangeFirst = digits[3];

  if (areaFirst === "0" || areaFirst === "1") {
    return {
      ok: false,
      message:
        "Enter a valid phone number. The area code cannot start with 0 or 1.",
    };
  }
  if (exchangeFirst === "0" || exchangeFirst === "1") {
    return {
      ok: false,
      message:
        "Enter a valid phone number. The middle three digits cannot start with 0 or 1.",
    };
  }
  if (/^(\d)\1{9}$/.test(digits)) {
    return { ok: false, message: "Enter a valid phone number." };
  }

  return { ok: true, normalized: digits };
}
